import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLayout } from "../../LayoutContext";
import { DeviceStoryDto } from "../dto/DeviceStoryDto";
import { DeviceVisualDto } from "../dto/DeviceVisualDto";
import DesktopIcons from "./DesktopIcons";
import { WindowTypeEnum } from "./enums/WindowTypeEnum";
import SimpleClassicyWindow from "./components/window/SimpleClassicyWindow";
import VisitorStoriesWindow from "./windows/VisitorStoriesWindow";
import SubmitStoryWindow from "./windows/SubmitStoryWindow";
import SimpleMoviePlayer from "./components/quicktime/SimpleMoviePlayer";
import { QuickTimeVideoEmbed } from "./components/quicktime/QuickTimeMovieEmbed";
import { ClassicyTextEditor } from "./components/text-editor/ClassicyTextEditor";
import LoadingOverlay from "./LoadingOverlay";
import WelcomeScreen from "./WelcomeScreen";
import { api } from "../../../interceptors/api";
import {
  UserStoriesProvider,
  useUserStories,
} from "./context/UserStoriesContext";
import "./styles/ImacDesktopShell.css";
import "./styles/_variables.scss";

const BOOT_TIME = 4 * 1000; // 4 seconds
const VISUAL_REFRESH_INTERVAL = 10 * 1000; // 10 seconds - check for new visuals
const SCREEN_WIDTH = 1600;
const SCREEN_HEIGHT = 900;

function ImacDesktopShellContent() {
  const { windowType } = useParams<{ windowType?: WindowTypeEnum }>();
  const navigate = useNavigate();
  const [booted, setBooted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openWindow, setOpenWindow] = useState<string | null>(null);
  const [deviceStoryAudio, setDeviceStoryAudio] = useState<string | null>(null);
  const [deviceVisual, setDeviceVisual] = useState<string | null>(null);
  const [openUserStories, setOpenUserStories] = useState<
    Array<{
      story: any;
      showText: boolean;
      showAudio: boolean;
    }>
  >([]);
  const { setExhibitionMode } = useLayout();
  const { loadStories, refreshStoriesWithVisuals } = useUserStories();
  const visualRefreshTimer = useRef<number>();

  useEffect(() => {
    setExhibitionMode(true);
    return () => {
      setExhibitionMode(false);
    };
  }, [setExhibitionMode]);

  // Fetch device story audio, device visual, and user stories during boot
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch device story audio
        const deviceStoryRes = await api.get<DeviceStoryDto>(
          "/api/device-story/device/4/exhibition/4",
        );
        if (deviceStoryRes.data.audioUrl) {
          setDeviceStoryAudio(deviceStoryRes.data.audioUrl);
        }

        // Fetch device visual
        const deviceVisualRes = await api.get<DeviceVisualDto>(
          "/api/device-visual/device/4/exhibition/4",
        );
        if (deviceVisualRes.data.visualUrl) {
          setDeviceVisual(deviceVisualRes.data.visualUrl);
        }

        // Load user stories (this happens during boot/loading screen)
        await loadStories();
      } catch (err) {
        console.error("Failed to load initial data:", err);
      }
    };

    fetchInitialData();
  }, [loadStories]);

  // Periodic device story and visual refresh (regenerated after new user story submissions)
  useEffect(() => {
    if (!booted || loading) return;

    let isMounted = true;

    const fetchDeviceData = () => {
      // Fetch device story audio
      api
        .get<DeviceStoryDto>("/api/device-story/device/4/exhibition/4")
        .then((res) => {
          if (!isMounted) return;
          if (res.data.audioUrl) {
            setDeviceStoryAudio(res.data.audioUrl);
          }
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error("Failed to refresh device story:", err);
        });

      // Fetch device visual
      api
        .get<DeviceVisualDto>("/api/device-visual/device/4/exhibition/4")
        .then((res) => {
          if (!isMounted) return;
          if (res.data.visualUrl) {
            setDeviceVisual(res.data.visualUrl);
          }
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error("Failed to refresh device visual:", err);
        });
    };

    // Fetch every 60 seconds
    const interval = window.setInterval(fetchDeviceData, 60_000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [booted, loading]);

  // Handling passed windowType param
  useEffect(() => {
    if (!windowType || windowType === WindowTypeEnum.Welcome) {
      setLoading(false);
      setBooted(false);
      setOpenWindow(null);
      return;
    }

    if (windowType === WindowTypeEnum.Desktop) {
      setLoading(false);
      setBooted(true);
      setOpenWindow(null);
      return;
    }

    if (booted) {
      setLoading(false);
      setOpenWindow(windowType);
      return;
    }

    setLoading(true);
    const t = window.setTimeout(() => {
      setLoading(false);
      setBooted(true);
      setOpenWindow(windowType);
    }, BOOT_TIME);

    // cleanup if they click Exit mid-boot
    return () => window.clearTimeout(t);
  }, [windowType, booted]);

  // Periodic refresh of user story visuals
  useEffect(() => {
    if (!booted || loading) return;

    const startVisualRefresh = () => {
      visualRefreshTimer.current = window.setInterval(() => {
        refreshStoriesWithVisuals();
      }, VISUAL_REFRESH_INTERVAL);
    };

    startVisualRefresh();

    return () => {
      window.clearInterval(visualRefreshTimer.current);
    };
  }, [booted, loading, refreshStoriesWithVisuals]);

  // Close user story windows when DeviceStory or SubmitStory windows open
  useEffect(() => {
    if (
      openWindow === WindowTypeEnum.DeviceStory ||
      openWindow === WindowTypeEnum.SubmitStory
    ) {
      setOpenUserStories([]);
    }
  }, [openWindow]);

  const handleExit = () => {
    navigate("/exhibits/imac/welcome", { replace: true });
  };

  const handleOpenWindow = (windowType: WindowTypeEnum) => {
    navigate(`/exhibits/imac/${windowType}`, { replace: true });
  };

  const handleCloseWindow = () => {
    //setOpenWindow(null);
    navigate("/exhibits/imac/desktop", { replace: true });
  };

  const handleOpenUserStory = (story: any) => {
    // Replace any currently open user story with the new one
    setOpenUserStories([
      { story, showText: true, showAudio: !!story.audioUrl },
    ]);
  };

  const closeUserStoryText = (id: number) => {
    setOpenUserStories((prev) =>
      prev
        .map((s) => (s.story.id === id ? { ...s, showText: false } : s))
        .filter((s) => s.showText || s.showAudio),
    );
  };

  const closeUserStoryAudio = (id: number) => {
    setOpenUserStories((prev) =>
      prev
        .map((s) => (s.story.id === id ? { ...s, showAudio: false } : s))
        .filter((s) => s.showText || s.showAudio),
    );
  };

  const getWindowTitle = (windowType: string) => {
    switch (windowType) {
      case WindowTypeEnum.DeviceStory:
        return "Device Story";
      case WindowTypeEnum.VisitorStories:
        return "Besuchergeschichten";
      case WindowTypeEnum.SubmitStory:
        return "Teile Deine Geschichte";
      default:
        return "Window";
    }
  };

  const getWindowDimensions = (windowType: string) => {
    switch (windowType) {
      case WindowTypeEnum.DeviceStory:
        return { width: 718, height: 600 };
      case WindowTypeEnum.SubmitStory:
        return { width: 900, height: 700 };
      case WindowTypeEnum.VisitorStories:
        return { width: 1340, height: 800 };
      default:
        return { width: 800, height: 600 };
    }
  };

  const getWindowPosition = (windowType: string) => {
    switch (windowType) {
      case WindowTypeEnum.DeviceStory:
        return {
          top: (SCREEN_HEIGHT - getWindowDimensions(windowType).height) / 2,
          left: (SCREEN_WIDTH - getWindowDimensions(windowType).width) / 2,
        };
      case WindowTypeEnum.SubmitStory:
        return {
          top: (SCREEN_HEIGHT - getWindowDimensions(windowType).height) / 2,
          left: (SCREEN_WIDTH - getWindowDimensions(windowType).width) / 2,
        };
      case WindowTypeEnum.VisitorStories:
        return { top: 48, left: 12 };
      default:
        return { top: 48, left: 12 };
    }
  };

  const renderWindowContent = (windowType: string) => {
    switch (windowType) {
      case WindowTypeEnum.DeviceStory:
        return deviceStoryAudio ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <QuickTimeVideoEmbed
              appId="device-story-player"
              name="iMac G3 Device Story"
              url={deviceStoryAudio}
              type="audio"
              visualUrl={deviceVisual || undefined}
              autoPlay={false}
              hideControls={false}
              controlsDocked={true}
              muted={false}
            />
          </div>
        ) : null;
      case WindowTypeEnum.VisitorStories:
        return <VisitorStoriesWindow onOpenStory={handleOpenUserStory} />;
      case WindowTypeEnum.SubmitStory:
        return <SubmitStoryWindow />;
      default:
        return null;
    }
  };

  return (
    <div className="imac-desktop-shell">
      {!booted && (
        <>
          <WelcomeScreen
            onOpen={(windowType: WindowTypeEnum) => {
              navigate(`/exhibits/imac/${windowType}`, { replace: true });
            }}
          />
        </>
      )}

      {loading && <LoadingOverlay />}

      {booted && (
        <>
          <div className="top-bar">
            <div className="exit-button" onClick={handleExit}>
              ⏻
            </div>
          </div>

          <DesktopIcons
            onOpen={(windowType: WindowTypeEnum) =>
              handleOpenWindow(windowType)
            }
          />

          {openWindow && (
            <SimpleClassicyWindow
              title={getWindowTitle(openWindow)}
              icon=""
              closable={true}
              zoomable={false}
              collapsable={false}
              scrollable={true}
              isActive={true}
              width={getWindowDimensions(openWindow).width}
              height={getWindowDimensions(openWindow).height}
              onClose={handleCloseWindow}
              left={getWindowPosition(openWindow).left || 12}
              top={getWindowPosition(openWindow).top || 48}
            >
              {renderWindowContent(openWindow)}
            </SimpleClassicyWindow>
          )}

          {openUserStories.map(({ story, showText, showAudio }) => (
            <div key={story.id}>
              {showText && (
                <SimpleClassicyWindow
                  title={story.titleDE || "Story"}
                  closable={true}
                  width={880}
                  height={564}
                  top={112}
                  left={42}
                  onClose={() => closeUserStoryText(story.id)}
                >
                  <ClassicyTextEditor content={story.textDE || ""} />
                </SimpleClassicyWindow>
              )}
              {showAudio && story.audioUrl && (
                <SimpleMoviePlayer
                  url={story.audioUrl}
                  name={"Story Audio"}
                  type="audio"
                  visualUrl={story.visualUrl}
                  top={132}
                  left={980}
                  width={340}
                  height={440}
                  onClose={() => closeUserStoryAudio(story.id)}
                />
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// Wrapper component with context provider
export default function ImacDesktopShell() {
  return (
    <UserStoriesProvider>
      <ImacDesktopShellContent />
    </UserStoriesProvider>
  );
}
